/**
 * Quick view trigger for the KWC Premium collection page.
 *
 * This theme already ships a full quick-add modal (assets/quick-add.js,
 * <quick-add-component>) that fetches the product page and morphs the
 * product details (media, price, variant picker, add-to-cart form) into a
 * <dialog>. Rather than building a second fetch/dialog implementation, this
 * component simply reuses that existing infrastructure: it locates the
 * sibling <quick-add-component> on the same product card and invokes its
 * public handleClick() to open the very same modal — giving us a
 * "quick view" entry point with zero duplicated network/dialog code.
 *
 * If quick add is disabled in theme settings (no <quick-add-component> is
 * rendered on the card), the button degrades to a plain link to the product
 * page so it is never a dead control.
 */
class QuickViewButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (/** @type {MouseEvent} */ event) => {
    const productCard = this.closest('product-card');
    const quickAdd = /** @type {any} */ (productCard?.querySelector('quick-add-component'));

    if (quickAdd && typeof quickAdd.handleClick === 'function') {
      event.preventDefault();
      event.stopPropagation();
      quickAdd.handleClick(event);
      return;
    }

    // No quick-add-component available (quick add disabled in theme settings):
    // fall back to navigating to the product page.
    const link = productCard?.querySelector('.product-card__link');
    if (link instanceof HTMLAnchorElement) {
      event.preventDefault();
      window.location.href = link.href;
    }
  };
}

if (!customElements.get('quick-view-button')) customElements.define('quick-view-button', QuickViewButton);
